import type { LocationRecord } from '@/shared/repositories/locations/locationsRepository'
import { toDateTimeLocalInputValue } from '@/shared/lib/date'

export type DateRange = { start: Date; end: Date }

const daysInUtcMonth = (year: number, month: number): number =>
  new Date(Date.UTC(year, month + 1, 0)).getUTCDate()

/**
 * Janela de pagamento de um local para o mês de referência.
 * Sem dia de corte → mês-calendário UTC de `reference`.
 * Com dia D → ]D mês anterior, D mês corrente] (fim inclusivo; `end` exclusivo para `<`).
 */
export const paymentCutoffRange = (
  estimatedPaymentDay: number | null,
  reference: Date,
): DateRange => {
  const year = reference.getUTCFullYear()
  const month = reference.getUTCMonth()

  if (estimatedPaymentDay == null || estimatedPaymentDay <= 0) {
    return {
      start: new Date(Date.UTC(year, month, 1)),
      end: new Date(Date.UTC(year, month + 1, 1)),
    }
  }

  const clampedCurrent = Math.min(estimatedPaymentDay, daysInUtcMonth(year, month))
  const clampedPrev = Math.min(estimatedPaymentDay, daysInUtcMonth(year, month - 1))

  return {
    start: new Date(Date.UTC(year, month - 1, clampedPrev + 1)),
    end: new Date(Date.UTC(year, month, clampedCurrent + 1)),
  }
}

/** Menor início e maior fim entre várias janelas (para alargar busca de entries). */
export const unionRange = (ranges: DateRange[]): DateRange | null => {
  if (ranges.length === 0) return null
  let { start, end } = ranges[0]!
  for (let i = 1; i < ranges.length; i++) {
    const range = ranges[i]!
    if (range.start < start) start = range.start
    if (range.end > end) end = range.end
  }
  return { start, end }
}

export const buildLocationCutoffRanges = (
  locations: Pick<LocationRecord, 'id' | 'estimatedPaymentDay'>[],
  reference: Date,
): Record<string, DateRange> =>
  Object.fromEntries(
    locations.map((loc) => [loc.id, paymentCutoffRange(loc.estimatedPaymentDay, reference)]),
  )

/** Converte janela UTC para valores datetime-local (fim inclusivo para exibição). */
export const cutoffRangeToDatetimeLocal = (range: DateRange): { start: string; end: string } => ({
  start: toDateTimeLocalInputValue(range.start),
  end: toDateTimeLocalInputValue(new Date(range.end.getTime() - 60_000)),
})

export const buildLocationCutoffRangesDatetimeLocal = (
  locations: Pick<LocationRecord, 'id' | 'estimatedPaymentDay'>[],
  reference: Date,
): Record<string, { start: string; end: string }> =>
  Object.fromEntries(
    locations.map((loc) => [
      loc.id,
      cutoffRangeToDatetimeLocal(paymentCutoffRange(loc.estimatedPaymentDay, reference)),
    ]),
  )
